import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminBillHeaderImage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [billHeaderImage, setBillHeaderImage] = useState("");
  const [imageType, setImageType] = useState("url"); // "url" or "upload"
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("adminToken");
  };

  // Check authentication on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
    }
    fetchCompanies();
  }, [navigate]);

  // Fetch all companies
  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch companies");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle company selection
  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    setSelectedCompany(company);
    const existingImage = company?.billHeaderImage || "";
    setBillHeaderImage(existingImage);
    
    // Check if existing image is base64 or URL
    if (existingImage && (existingImage.startsWith('data:image') || existingImage.startsWith('blob:'))) {
      setImageType("upload");
      setImagePreview(existingImage);
    } else {
      setImageType("url");
      setImagePreview(existingImage);
    }
    setImageFile(null);
  };

  // Handle image URL change
  const handleImageChange = (e) => {
    setBillHeaderImage(e.target.value);
    setImagePreview(e.target.value);
  };

  // Compress image function
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      
      // Validate file size (max 10MB before compression)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size should be less than 10MB");
        return;
      }

      setImageFile(file);
      
      try {
        // Show loading message
        toast.info("Compressing image...");
        
        // Compress the image
        const compressedBase64 = await compressImage(file, 1200, 0.7);
        
        // Check compressed size (base64 is ~33% larger than original)
        const compressedSize = (compressedBase64.length * 3) / 4;
        if (compressedSize > 2 * 1024 * 1024) {
          // If still too large, compress more aggressively
          const moreCompressed = await compressImage(file, 800, 0.6);
          setBillHeaderImage(moreCompressed);
          setImagePreview(moreCompressed);
          toast.success(`Image compressed to ${(moreCompressed.length * 3 / 4 / 1024).toFixed(0)}KB`);
        } else {
          setBillHeaderImage(compressedBase64);
          setImagePreview(compressedBase64);
          toast.success(`Image compressed to ${(compressedSize / 1024).toFixed(0)}KB`);
        }
      } catch (error) {
        console.error("Image compression error:", error);
        toast.error("Failed to compress image. Please try a smaller image.");
        // Fallback to original if compression fails
        const reader = new FileReader();
        reader.onloadend = () => {
          setBillHeaderImage(reader.result);
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Save bill header image
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    if (!billHeaderImage.trim()) {
      toast.error(imageType === "url" ? "Please enter an image URL" : "Please upload an image");
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      
      await api.put(
        `/admin/companies/${selectedCompany._id}/bill-header-image`,
        { billHeaderImage: billHeaderImage.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Bill header image updated successfully!");
      
      // Update local state
      const updatedCompanies = companies.map(c => 
        c._id === selectedCompany._id 
          ? { ...c, billHeaderImage: billHeaderImage.trim() }
          : c
      );
      setCompanies(updatedCompanies);
      setSelectedCompany({ ...selectedCompany, billHeaderImage: billHeaderImage.trim() });
    } catch (error) {
      console.error("Save bill header image error:", error);
      toast.error(error.response?.data?.message || "Failed to save bill header image");
    } finally {
      setIsSaving(false);
    }
  };

  // Remove bill header image
  const handleRemove = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    if (!window.confirm("Are you sure you want to remove the bill header image?")) {
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();
      
      await api.put(
        `/admin/companies/${selectedCompany._id}/bill-header-image`,
        { billHeaderImage: "" },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Bill header image removed successfully!");
      
      // Update local state
      const updatedCompanies = companies.map(c => 
        c._id === selectedCompany._id 
          ? { ...c, billHeaderImage: "" }
          : c
      );
      setCompanies(updatedCompanies);
      setSelectedCompany({ ...selectedCompany, billHeaderImage: "" });
      setBillHeaderImage("");
      setImagePreview("");
      setImageFile(null);
      setImageType("url");
    } catch (error) {
      console.error("Remove bill header image error:", error);
      toast.error(error.response?.data?.message || "Failed to remove bill header image");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Helmet><title>Bill Header Image Management - Admin Panel</title></Helmet>
      
      <div className="min-h-screen bg-brand-background p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-brand-header">Bill Header Image Management</h1>
                <p className="text-gray-600 mt-1">Upload and manage bill header images for companies</p>
              </div>
              <button
                onClick={() => navigate("/admin")}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading companies...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Company Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Company
                  </label>
                  <select
                    value={selectedCompany?._id || ""}
                    onChange={(e) => handleCompanySelect(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all"
                  >
                    <option value="">-- Select a company --</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.shopName || company.username} {company.billHeaderImage ? "(Has Image)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCompany && (
                  <>
                    {/* Image Upload Options */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Bill Header Image
                      </label>
                      
                      {/* Radio buttons for URL or Upload */}
                      <div className="flex gap-4 mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="imageType"
                            value="url"
                            checked={imageType === 'url'}
                            onChange={(e) => {
                              setImageType('url');
                              setImageFile(null);
                              if (!billHeaderImage.startsWith('data:image') && !billHeaderImage.startsWith('blob:')) {
                                setImagePreview(billHeaderImage);
                              } else {
                                setBillHeaderImage("");
                                setImagePreview("");
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span>Use URL</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="imageType"
                            value="upload"
                            checked={imageType === 'upload'}
                            onChange={(e) => {
                              setImageType('upload');
                              setBillHeaderImage("");
                              setImagePreview("");
                            }}
                            className="w-4 h-4"
                          />
                          <span>Upload from Computer</span>
                        </label>
                      </div>

                      {/* URL Input */}
                      {imageType === 'url' ? (
                        <div>
                          <input
                            type="url"
                            value={billHeaderImage.startsWith('data:image') || billHeaderImage.startsWith('blob:') ? "" : billHeaderImage}
                            onChange={handleImageChange}
                            placeholder="https://example.com/bill-header-image.png"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter the complete URL of the bill header image. The image will be displayed at the top of all bills for this company.
                          </p>
                        </div>
                      ) : (
                        /* File Upload Input */
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Select an image file from your computer. Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB.
                          </p>
                          {imageFile && (
                            <p className="text-xs text-green-600 mt-1">
                              Selected: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Image Preview */}
                    {(imagePreview || billHeaderImage) && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Preview
                        </label>
                        <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                          <img
                            src={imagePreview || billHeaderImage}
                            alt="Bill Header Preview"
                            className="w-full h-auto max-h-64 object-contain rounded-lg"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const errorDiv = e.target.nextElementSibling;
                              if (errorDiv) {
                                errorDiv.classList.remove('hidden');
                              }
                            }}
                          />
                          <div className="hidden text-center py-8 text-gray-500">
                            <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p>Image failed to load. {imageType === 'url' ? 'Please check the URL.' : 'Please try uploading again.'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Company Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-700 mb-2">Company Information</h3>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Company Name:</span>
                          <span className="ml-2 font-semibold">{selectedCompany.shopName || selectedCompany.username}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Username:</span>
                          <span className="ml-2 font-semibold">{selectedCompany.username}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <span className="ml-2 font-semibold">{selectedCompany.email}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Current Status:</span>
                          <span className={`ml-2 font-semibold ${selectedCompany.billHeaderImage ? 'text-green-600' : 'text-gray-500'}`}>
                            {selectedCompany.billHeaderImage ? 'Has Header Image' : 'No Header Image'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-1 px-6 py-3 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSaving ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Save Image</span>
                          </>
                        )}
                      </button>
                      {selectedCompany.billHeaderImage && (
                        <button
                          onClick={handleRemove}
                          disabled={isSaving}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Remove Image
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminBillHeaderImage;

